import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  FiPlus,
  FiTrash2,
  FiSave,
  FiAlertCircle,
  FiClock,
  FiPackage,
  FiX,
  FiCheckCircle,
  FiEdit2,
  FiInfo,
} from "react-icons/fi";
import { toast } from "react-toastify";
import appointment from "../../services/appointment";
import { useAuth } from "../../context/AuthContext";

interface ProfessionalService {
  id?: number;
  name: string;
  duration_minutes: number;
  is_active?: boolean;
}

const SERVICE_SUGGESTIONS = [
  { name: "Consulta Individual", duration: 60 },
  { name: "Aula Particular", duration: 50 },
  { name: "Sessão de Terapia", duration: 60 },
  { name: "Avaliação Inicial", duration: 90 },
  { name: "Retorno", duration: 30 },
  { name: "Atendimento Express", duration: 30 },
  { name: "Sessão em Grupo", duration: 90 },
  { name: "Workshop", duration: 120 },
];

const DURATION_PRESETS = [15, 30, 45, 60, 90, 120, 180];

export interface ServiceTabHandle {
  save: () => Promise<boolean>;
}

interface ServiceTabProps {
  hideSaveButton?: boolean;
  hideIndicators?: boolean;
  hideFooter?: boolean;
  onStateChange?: (state: {
    hasChanges: boolean;
    saving: boolean;
    hasErrors: boolean;
  }) => void;
}

const ServiceTab = forwardRef<ServiceTabHandle, ServiceTabProps>(
  (
    {
      hideSaveButton = false,
      hideIndicators = false,
      hideFooter = false,
      onStateChange,
    },
    ref,
  ) => {
    const { getUser } = useAuth();
    const [services, setServices] = useState<ProfessionalService[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [errors, setErrors] = useState<Record<number, string>>({});
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [editingDuration, setEditingDuration] = useState<number | null>(null);
    const [tooltipVisible, setTooltipVisible] = useState<string | null>(null);

    useEffect(() => {
      loadServices();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      onStateChange?.({
        hasChanges,
        saving,
        hasErrors: Object.keys(errors).length > 0,
      });
    }, [hasChanges, saving, errors, onStateChange]);

    const loadServices = async () => {
      const user = getUser();
      if (!user?.id) return;

      setLoading(true);
      try {
        const data = await appointment.getProfessionalServices(String(user.id));
        setServices(data || []);
        setHasChanges(false);
      } catch (error) {
        console.error("Erro ao carregar serviços:", error);
        toast.error("Erro ao carregar serviços");
      } finally {
        setLoading(false);
      }
    };

    const validateService = (
      index: number,
      service: ProfessionalService,
    ): string | null => {
      if (!service.name || !service.name.trim()) {
        return "Nome do serviço é obrigatório";
      }
      if (!service.duration_minutes || service.duration_minutes <= 0) {
        return "Duração deve ser maior que 0";
      }
      if (service.duration_minutes > 480) {
        return "Duração máxima é 480 minutos (8 horas)";
      }

      const duplicates = services.filter(
        (s, i) =>
          i !== index &&
          s.name.trim().toLowerCase() === service.name.trim().toLowerCase(),
      );
      if (duplicates.length > 0) {
        return "Já existe um serviço com este nome";
      }

      return null;
    };

    const handleAdd = () => {
      const newService: ProfessionalService = {
        name: "",
        duration_minutes: 60,
      };
      setServices([...services, newService]);
      setHasChanges(true);
    };

    const handleAddFromSuggestion = (
      suggestion: (typeof SERVICE_SUGGESTIONS)[0],
    ) => {
      const newService: ProfessionalService = {
        name: suggestion.name,
        duration_minutes: suggestion.duration,
      };
      setServices([...services, newService]);
      setHasChanges(true);
      setShowSuggestions(false);
    };

    const handleRemove = (index: number) => {
      const newServices = services.filter((_, i) => i !== index);
      setServices(newServices);
      setHasChanges(true);

      const newErrors = { ...errors };
      delete newErrors[index];
      setErrors(newErrors);
    };

    const handleChange = (
      index: number,
      field: keyof ProfessionalService,
      value: string | number,
    ) => {
      const newServices = [...services];
      newServices[index] = {
        ...newServices[index],
        [field]: field === "duration_minutes" ? Number(value) : value,
      };
      setServices(newServices);
      setHasChanges(true);

      const error = validateService(index, newServices[index]);
      if (error) {
        setErrors({ ...errors, [index]: error });
      } else {
        const newErrors = { ...errors };
        delete newErrors[index];
        setErrors(newErrors);
      }
    };

    const handleSave = async (): Promise<boolean> => {
      const user = getUser();
      if (!user?.id) return false;

      const validationErrors: Record<number, string> = {};
      services.forEach((service, index) => {
        const error = validateService(index, service);
        if (error) {
          validationErrors[index] = error;
        }
      });

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        toast.error("Corrija os erros antes de salvar");
        return false;
      }

      setSaving(true);
      try {
        const servicesToSave = services.map(
          ({ id, is_active, ...rest }) => rest,
        );
        const result = await appointment.setProfessionalServices(
          String(user.id),
          servicesToSave,
        );

        if (result) {
          toast.success("Serviços salvos com sucesso!");
          await loadServices();
          return true;
        } else {
          toast.error("Erro ao salvar serviços");
          return false;
        }
      } catch (error) {
        console.error("Erro ao salvar serviços:", error);
        toast.error("Erro ao salvar serviços");
        return false;
      } finally {
        setSaving(false);
      }
    };

    const handleCancel = () => {
      loadServices();
      setErrors({});
    };

    const formatDuration = (minutes: number): string => {
      if (minutes < 60) return `${minutes} min`;
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    };

    const InfoTooltip = ({ id, text }: { id: string; text: string }) => (
      <div className="relative inline-block">
        <button
          type="button"
          onMouseEnter={() => setTooltipVisible(id)}
          onMouseLeave={() => setTooltipVisible(null)}
          onClick={(e) => {
            e.stopPropagation();
            setTooltipVisible(tooltipVisible === id ? null : id);
          }}
          className="ml-2 inline-flex items-center justify-center rounded-full text-gray-400 transition hover:text-purple-600 dark:hover:text-purple-400"
        >
          <FiInfo size={16} />
        </button>
        {tooltipVisible === id && (
          <div className="absolute left-0 top-6 z-50 w-72 rounded-lg border border-stroke bg-white p-3 text-xs leading-relaxed text-gray-600 shadow-lg dark:border-strokedark dark:bg-boxdark dark:text-gray-300">
            {text}
            <div className="absolute -top-1 left-4 h-2 w-2 rotate-45 border-l border-t border-stroke bg-white dark:border-strokedark dark:bg-boxdark" />
          </div>
        )}
      </div>
    );

    useImperativeHandle(ref, () => ({
      save: handleSave,
    }));

    if (loading) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-solid border-purple-600 border-t-transparent"></div>
        </div>
      );
    }

    return (
      <div className="space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <div className="flex items-center">
              <h4 className="text-lg font-bold text-black dark:text-white sm:text-xl lg:text-2xl">
                Serviços de Agendamento
              </h4>
              <InfoTooltip
                id="services-info"
                text="Configure os tipos de serviços que você oferece. Cada serviço pode ter nome e duração personalizados. Seus alunos poderão escolher entre estes serviços ao fazer um agendamento."
              />
            </div>
            <p className="mt-1.5 text-xs leading-relaxed text-gray-600 dark:text-gray-400 sm:mt-2 sm:text-sm">
              Configure os serviços que você oferece para agendamento através do
              APP do aluno ou por meio do seu link personalizado
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3">
            <button
              type="button"
              onClick={() => setShowSuggestions(!showSuggestions)}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-stroke bg-white px-4 py-2.5 text-sm font-semibold text-black shadow-sm transition-all hover:bg-gray-50 hover:shadow-md dark:border-strokedark dark:bg-boxdark dark:text-white dark:hover:bg-meta-4 sm:px-5"
            >
              <FiPackage size={16} className="sm:w-[18px] sm:h-[18px]" />
              Sugestões
            </button>

            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 sm:px-5"
            >
              <FiPlus size={16} className="sm:w-[18px] sm:h-[18px]" />
              Novo Serviço
            </button>
          </div>
        </div>

        {/* Sugestões de Serviços */}
        {showSuggestions && (
          <div className="rounded-lg border border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100 p-4 shadow-sm dark:border-purple-800 dark:from-purple-900/20 dark:to-purple-800/10 sm:rounded-xl sm:p-6">
            <div className="mb-4 flex items-center justify-between sm:mb-5">
              <div className="flex items-center gap-2.5 sm:gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-500 text-white shadow-sm sm:h-10 sm:w-10">
                  <FiPackage size={16} className="sm:w-5 sm:h-5" />
                </div>
                <h4 className="text-sm font-bold text-purple-900 dark:text-purple-100 sm:text-base">
                  Serviços Sugeridos
                </h4>
              </div>
              <button
                onClick={() => setShowSuggestions(false)}
                className="rounded-lg p-1.5 text-purple-600 transition hover:bg-purple-200 dark:text-purple-400 dark:hover:bg-purple-800 sm:p-2"
              >
                <FiX size={18} className="sm:w-5 sm:h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3 lg:grid-cols-4">
              {SERVICE_SUGGESTIONS.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleAddFromSuggestion(suggestion)}
                  className="group flex items-center justify-between rounded-lg border border-purple-200 bg-white p-3 text-left transition-all hover:border-purple-400 hover:shadow-md dark:border-purple-700 dark:bg-boxdark dark:hover:border-purple-500 sm:rounded-xl sm:p-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-900 truncate dark:text-white sm:text-sm">
                      {suggestion.name}
                    </p>
                    <p className="mt-0.5 flex items-center text-[10px] text-gray-500 dark:text-gray-400 sm:mt-1 sm:text-xs">
                      <FiClock className="mr-1 sm:mr-1.5" size={10} />
                      {formatDuration(suggestion.duration)}
                    </p>
                  </div>
                  <div className="ml-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-purple-100 transition group-hover:bg-purple-600 dark:bg-purple-900/50 sm:ml-3 sm:h-8 sm:w-8">
                    <FiPlus
                      size={14}
                      className="text-purple-600 transition group-hover:scale-110 group-hover:text-white dark:text-purple-400 sm:w-[18px] sm:h-[18px]"
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Resumo */}
        {!hideIndicators && services.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-5">
            <div className="rounded-lg border border-stroke bg-gradient-to-br from-blue-50 to-blue-100 p-4 shadow-sm dark:border-strokedark dark:from-blue-900/20 dark:to-blue-800/20 sm:rounded-xl sm:p-6">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-blue-500 text-white shadow-lg sm:h-16 sm:w-16 sm:rounded-xl">
                  <FiPackage size={20} className="sm:w-[25px] sm:h-[25px]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-2xl font-bold text-blue-900 dark:text-blue-100 sm:text-4xl">
                    {services.length}
                  </p>
                  <p className="mt-0.5 text-xs font-medium text-blue-700 dark:text-blue-300 sm:mt-1 sm:text-sm">
                    Serviços cadastrados
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-stroke bg-gradient-to-br from-green-50 to-green-100 p-4 shadow-sm dark:border-strokedark dark:from-green-900/20 dark:to-green-800/20 sm:rounded-xl sm:p-6">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-green-500 text-white shadow-lg sm:h-16 sm:w-16 sm:rounded-xl">
                  <FiClock size={20} className="sm:w-[25px] sm:h-[25px]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-2xl font-bold text-green-900 dark:text-green-100 sm:text-4xl">
                    {Math.round(
                      services.reduce((acc, s) => acc + s.duration_minutes, 0) /
                        services.length || 0,
                    )}
                  </p>
                  <p className="mt-0.5 text-xs font-medium text-green-700 dark:text-green-300 sm:mt-1 sm:text-sm">
                    Duração média (min)
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-stroke bg-gradient-to-br from-purple-50 to-purple-100 p-4 shadow-sm dark:border-strokedark dark:from-purple-900/20 dark:to-purple-800/20 sm:rounded-xl sm:p-6">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-purple-500 text-white shadow-lg sm:h-16 sm:w-16 sm:rounded-xl">
                  <FiCheckCircle size={20} className="sm:w-[25px] sm:h-[25px]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-2xl font-bold text-purple-900 dark:text-purple-100 sm:text-4xl">
                    {services.filter((s) => s.name.trim()).length}
                  </p>
                  <p className="mt-0.5 text-xs font-medium text-purple-700 dark:text-purple-300 sm:mt-1 sm:text-sm">
                    Completos
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Serviços */}
        <div className="space-y-3 sm:space-y-4">
          {services.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center dark:border-gray-700 dark:bg-gray-800/50 sm:rounded-xl sm:p-20">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30 sm:mb-5 sm:h-24 sm:w-24">
                <FiPackage
                  size={36}
                  className="text-purple-600 dark:text-purple-400 sm:w-12 sm:h-12"
                />
              </div>
              <h4 className="mb-2 text-base font-bold text-gray-900 dark:text-white sm:text-xl">
                Nenhum serviço cadastrado
              </h4>
              <p className="mb-5 text-xs text-gray-600 dark:text-gray-400 sm:mb-6 sm:text-sm">
                Adicione serviços para começar a receber agendamentos
              </p>
              <button
                type="button"
                onClick={handleAdd}
                className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow-md sm:px-6 sm:py-3"
              >
                <FiPlus size={18} className="sm:w-5 sm:h-5" />
                Adicionar Primeiro Serviço
              </button>
            </div>
          ) : (
            services.map((service, index) => (
              <div
                key={index}
                className={`rounded-lg border bg-white p-4 shadow-sm transition-all hover:shadow-md dark:bg-boxdark sm:rounded-xl sm:p-6 ${
                  errors[index]
                    ? "border-red-300 ring-2 ring-red-100 dark:border-red-800 dark:ring-red-900/30"
                    : "border-stroke dark:border-strokedark"
                }`}
              >
                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
                    {/* Nome do Serviço */}
                    <div className="lg:col-span-7">
                      <label className="mb-2.5 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Nome do Serviço <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={service.name}
                        onChange={(e) =>
                          handleChange(index, "name", e.target.value)
                        }
                        placeholder="Ex: Consulta Individual, Sessão de Terapia..."
                        className={`w-full rounded-lg border bg-transparent px-4 py-3 text-sm outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:bg-form-input dark:focus:ring-purple-800 ${
                          errors[index]
                            ? "border-red-500"
                            : "border-stroke dark:border-form-strokedark"
                        }`}
                      />
                    </div>

                    {/* Duração */}
                    <div className="lg:col-span-4">
                      <label className="mb-2.5 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Duração <span className="text-red-500">*</span>
                      </label>

                      {editingDuration === index ? (
                        <div className="relative">
                          <FiClock
                            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                            size={18}
                          />
                          <input
                            type="number"
                            min="1"
                            max="480"
                            value={service.duration_minutes}
                            onChange={(e) =>
                              handleChange(
                                index,
                                "duration_minutes",
                                parseInt(e.target.value) || 0,
                              )
                            }
                            onBlur={() => setEditingDuration(null)}
                            autoFocus
                            className={`w-full rounded-lg border bg-transparent py-3 pl-12 pr-20 text-sm outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:bg-form-input dark:focus:ring-purple-800 ${
                              errors[index]
                                ? "border-red-500"
                                : "border-stroke dark:border-form-strokedark"
                            }`}
                          />
                          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                            minutos
                          </span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditingDuration(index)}
                          className="group w-full rounded-lg border border-stroke bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-700 transition-all hover:bg-gray-100 hover:border-purple-300 hover:shadow-sm dark:border-strokedark dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          <FiClock className="mr-2 inline" size={16} />
                          {formatDuration(service.duration_minutes)}
                          <FiEdit2
                            className="ml-2 inline opacity-0 group-hover:opacity-100 transition"
                            size={14}
                          />
                        </button>
                      )}
                    </div>

                    {/* Botão Remover */}
                    <div className="lg:col-span-1 flex items-end">
                      <button
                        type="button"
                        onClick={() => handleRemove(index)}
                        className="w-full lg:w-auto flex items-center justify-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-3 text-sm font-semibold text-red-700 transition-all hover:bg-red-100 hover:border-red-400 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                        title="Remover serviço"
                      >
                        <FiTrash2 size={18} />
                        <span className="lg:hidden">Remover</span>
                      </button>
                    </div>
                  </div>

                  {/* Presets de Duração */}
                  {editingDuration !== index && (
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="flex-shrink-0 text-xs font-semibold text-gray-500 dark:text-gray-400">
                        Atalhos:
                      </span>
                      <div className="flex overflow-x-auto gap-2 pb-1 -mx-1 px-1 scrollbar-hide min-w-0 flex-1 [scrollbar-width:none] [-webkit-overflow-scrolling:touch]">
                        {DURATION_PRESETS.map((duration) => (
                          <button
                            key={duration}
                            type="button"
                            onClick={() =>
                              handleChange(index, "duration_minutes", duration)
                            }
                            className={`flex-shrink-0 rounded-lg px-4 py-2 text-xs font-bold transition-all touch-manipulation ${
                              service.duration_minutes === duration
                                ? "bg-purple-600 text-white shadow-md scale-105"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300 hover:shadow-sm dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                            }`}
                          >
                            {duration}min
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mensagem de Erro */}
                  {errors[index] && (
                    <div className="flex items-start gap-3 rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
                      <FiAlertCircle
                        className="mt-0.5 shrink-0 text-red-600 dark:text-red-400"
                        size={18}
                      />
                      <p className="text-sm font-semibold text-red-700 dark:text-red-400">
                        {errors[index]}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Botões de Ação */}
        {!hideFooter && services.length > 0 && (
          <div className="flex flex-col-reverse gap-2.5 border-t border-stroke pt-4 sm:flex-row sm:gap-3 sm:justify-end sm:pt-6 dark:border-strokedark">
            {hasChanges && (
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 dark:border-strokedark dark:bg-boxdark dark:text-gray-300 dark:hover:bg-meta-4 sm:px-6 sm:py-3"
              >
                <FiX size={16} className="sm:w-[18px] sm:h-[18px]" />
                Cancelar
              </button>
            )}

            {!hideSaveButton && (
              <button
                type="button"
                onClick={handleSave}
                disabled={
                  saving || Object.keys(errors).length > 0 || !hasChanges
                }
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:px-8 sm:py-3"
              >
                {saving ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <FiSave size={16} className="sm:w-[18px] sm:h-[18px]" />
                    Salvar Serviços
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    );
  },
);

export default ServiceTab;
